// MIDAS V3 — Sources row ("ZDROJE") under an assistant answer.
//
// Lifted out of ResponseCardV3 (MIDAS-ANSWER-FORMAT-1 r5) when the row grew
// the src pills; the page/sheet source-card strip itself is unchanged.
//
// Layout = Paper "MIDAS answer format", board S10, frame "Variant E" →
// "E sources" (get_computed_styles):
//   section : 1px top hairline #EEEBE4 → --border-soft, padding-top 10,
//             column gap 8, 12 px below the prose (8 px p margin + 4 here)
//   eyebrow : "ZDROJE" — JetBrains Mono 10/12, 700, 0.12em, #8C8983 → --faint
//   row     : flex-wrap, gap 8 — page/sheet cards first (today's strip),
//             then the <src-ref> pills: logic, then live (orderSrcRefs)
// The cards keep their stretch alignment (equal heights); the pills centre
// themselves on the row (`.src-ref { align-self: center }`, src-ref.css).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ResponseSourceCardV3 from "./ResponseSourceCardV3";
import { SrcRefV3 } from "./SrcRefV3";
import type { SrcRefPayload } from "./srcChipsGrammar";
import type { ResponseCardSource } from "./ResponseCardV3";

export interface ResponseSourcesRowV3Props {
  sources?: ResponseCardSource[];
  srcRefs?: SrcRefPayload[];
  /** Chrome-less assistant turn (the only V3 consumer) — no side gutter. */
  flat: boolean;
}

const ResponseSourcesRowV3: React.FC<ResponseSourcesRowV3Props> = ({
  sources,
  srcRefs,
  flat,
}) => {
  const { translate } = useTranslation("chatBot");
  const cards = sources || [];
  const pills = srcRefs || [];
  if (cards.length === 0 && pills.length === 0) return null;

  return (
    <div
      data-testid="midas-v3-sources-row"
      style={{
        margin: flat ? "4px 0 0" : "4px 18px 0",
        padding: flat ? "10px 0 4px" : "10px 0 16px",
        borderTop: "1px solid var(--border-soft)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 700,
          lineHeight: "12px",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--faint)",
        }}
      >
        {translate("midas.v3.responseCard.sourcesEyebrow", {
          defaultValue: "Sources",
        }) as string}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "stretch",
        }}
      >
        {cards.map((src, i) => (
          <ResponseSourceCardV3
            key={`${src.page}-${i}`}
            label={src.label}
            page={src.page}
            name={src.name}
            imageUrl={src.imageUrl}
            onClick={src.onClick}
          />
        ))}
        {pills.map((ref) => (
          <SrcRefV3
            key={`src-${ref.kind}-${ref.time ?? ""}`}
            kind={ref.kind}
            time={ref.time}
          />
        ))}
      </div>
    </div>
  );
};

export default ResponseSourcesRowV3;
